import { Reference } from '@approvalmax/types';
import { domain, schemas } from 'modules/data';
import { useMemo } from 'react';
import { useGetXeroBudgets } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

export interface MappedBudget {
    id: string;
    name: string;
    origin: domain.BudgetOrigin;
    checkPeriod?: domain.BudgetCheckPeriod;
    lastSyncDate?: string;
    modifiedDate: string;
    constraints: BudgetConstraint[];
    syncStatus?: domain.BudgetSyncStatus;
    checkPeriodFirstMonth?: number;
    checkPeriodFrom?: domain.BudgetPeriod;
    checkPeriodTill?: domain.BudgetPeriod;
    isEnabled: boolean;
    zeroAmountHandlingType?: domain.BudgetZeroAmountHandlingType;
    periodFrom?: domain.BudgetPeriod;
    periodTill?: domain.BudgetPeriod;
}

interface BudgetConstraint {
    value?: Reference;
    fieldId: string;
    fieldName: string;
    fieldPurpose: domain.FieldSystemPurpose;
}

const mapBudgetData = (budget: domain.Budget): MappedBudget => {
    return {
        isEnabled: budget.isEnabled,
        id: budget.budgetId,
        name: budget.name,
        origin: budget.origin,
        checkPeriod: budget.checkPeriod,
        lastSyncDate: budget.lastSyncDate,
        modifiedDate: budget.modifiedDate,
        syncStatus: budget.synchronizationStatus,
        checkPeriodFirstMonth: budget.checkPeriodFirstMonth,
        checkPeriodFrom: budget.checkPeriodFrom,
        checkPeriodTill: budget.checkPeriodTill,
        constraints: (budget.cutset || []).map((c) => ({
            fieldId: c.fieldId!,
            fieldName: c.fieldName,
            fieldPurpose: schemas.field.mapFieldSystemPurpose(c.fieldPurpose),
            value: c.constraint ? { id: c.constraint.id, text: c.constraint.value } : undefined,
        })),
        zeroAmountHandlingType: budget.zeroAmountHandlingType,
        periodFrom: budget.periodFrom,
        periodTill: budget.periodTill,
    };
};

export const useBudgets = () => {
    const company = useCompanyBasedOnUrl();

    const { data, ...params } = useGetXeroBudgets({
        query: {
            companyId: company.id,
        },
    });

    const mappedData = useMemo(() => (data?.data.budgets || []).map(mapBudgetData), [data]);

    return {
        data: mappedData,
        ...params,
    };
};
